import { InferredStringDataTypeName } from "./inferStringDataType";

export const hashAlgorithms = ["SHA-1", "SHA-256", "SHA-384", "SHA-512", "MD5", "SHA256-OF-MD5", "SHA256-OF-MD5HEX", "SHA256-OF-MD5BASE64"] as const;
export type HashAlgorithm = (typeof hashAlgorithms)[number];

export interface EncodingStepBase {
  encodingType: 'reformat' | 'hash' | 'base64' | 'hex' | 'removeSpaces' | 'replaceSpaces' | 'lowercase' | 'uppercase' | 'urlEncode' | 'htmlEncode'; // 'lettersOnly' | 'alphanumericOnly' | 'asciiLettersOnly' | 'digitsOnly' | 
}

export interface CoerceKnownDataTypeToFormatEncodingStep extends EncodingStepBase {
  encodingType: 'reformat';
  formatType: string;
  asFormattedType: string;
}

export interface HashEncodingStep extends EncodingStepBase {
  encodingType: 'hash';
  subtype: HashAlgorithm;
}

export type Base64EncodingSubtype = "Full" | "AlignedStart" | "RemovingFirstTwoBits" | "RemovingFirstFourBits";

export interface Base64EncodingStep extends EncodingStepBase {
  encodingType: 'base64';
  from: 'hex' | 'binary' | 'utf8';
  variant?: 'url';
  subtype: Base64EncodingSubtype;
}


export interface RemoveSpacesEncodingStep extends EncodingStepBase {
  encodingType: 'removeSpaces';
}

export interface ReplaceSpacesEncodingStep extends EncodingStepBase {
  encodingType: 'replaceSpaces';
  replacement: string;
}

export interface HexEncodingStep extends EncodingStepBase {
  encodingType: 'hex';
}

export interface LowercaseEncodingStep extends EncodingStepBase {
  encodingType: 'lowercase';
}

export interface UppercaseEncodingStep extends EncodingStepBase {
  encodingType: 'uppercase';
}

export interface UrlEncodingStep extends EncodingStepBase {
  encodingType: 'urlEncode';
}
export interface HtmlEncodingStep extends EncodingStepBase {
  encodingType: 'htmlEncode';
}

export type EncodingStep =
  CoerceKnownDataTypeToFormatEncodingStep |
  HashEncodingStep |
  Base64EncodingStep |
  HexEncodingStep |
  RemoveSpacesEncodingStep |
  ReplaceSpacesEncodingStep |
  LowercaseEncodingStep |
  UppercaseEncodingStep |
  UrlEncodingStep |
  HtmlEncodingStep |
  never;

const hashAlgorithmLc = {
  "SHA-1": "sha1",
  "SHA-256": "sha256",
  "SHA-384": "sha384",
  "SHA-512": "sha512",
  "MD5": "md5",
  "SHA256-OF-MD5": "sha256-of-md5",
  "SHA256-OF-MD5HEX": "sha256-of-md5hex",
  "SHA256-OF-MD5BASE64": "sha256-of-md5base64",
  } as const satisfies Record<HashAlgorithm, string>;

type EncodingFunctionString = typeof hashAlgorithmLc[HashAlgorithm] | Exclude<EncodingStepBase["encodingType"], "hash"> | "base64Url" | "hexToBinary";
type LowResEncodingNames = 'hash' | 'ipV6' | 'ipV4ToDecimal';
export const describeEncodingStepUsingFunctionNotation = ({
  encodingStep, value,
  hideDetails=false,
  fnNotation = (name: EncodingFunctionString | LowResEncodingNames, ...args: string[]) => `${name}(${args.join(", ")})`
}: {
  encodingStep: EncodingStep;
  value: string;
  hideDetails?: boolean;
  fnNotation?: (name: EncodingFunctionString | LowResEncodingNames, ...args: string[]) => string
}) => {
  switch (encodingStep.encodingType) {
    case 'hash': return fnNotation( hideDetails ? 'hash' : hashAlgorithmLc[encodingStep.subtype], `${value}`);
    case 'base64': {
      const encodingType = encodingStep.variant === 'url' ? `base64Url` : encodingStep.encodingType;
      const valueString = encodingStep.from === 'hex' ? fnNotation('hexToBinary', value) : value;
      switch (encodingStep.subtype) {
        case 'Full':
        case 'AlignedStart': return fnNotation(encodingType, valueString);
        case 'RemovingFirstTwoBits': return fnNotation(encodingType, `${valueString}<<2`);
        case 'RemovingFirstFourBits': return  fnNotation(encodingType, `${valueString}<<4`);
      }}
      break;
    case 'replaceSpaces': return fnNotation(`replaceSpaces`, value, `'${encodingStep.replacement}'`);
    case 'reformat': return encodingStep.formatType === encodingStep.asFormattedType ?
      `${encodingStep.formatType}=“${encodingStep.asFormattedType}”` :
      `“${encodingStep.asFormattedType}”⇐${encodingStep.formatType}(${value})`;
    default:
      return fnNotation(encodingStep.encodingType, value);
  }
}

export const describeEncodedSearchTermUsingFunctionNotationRaw = ({encodingSteps, rawValue, hideDetails, fnNotation}: {
  encodingSteps: EncodingStep[] | undefined;
  rawValue: string;
  hideDetails?: boolean;
  fnNotation?: (name: EncodingFunctionString | LowResEncodingNames, ...args: string[]) => string
}) =>
  (encodingSteps ?? []).reduce(
    (value, encodingStep) => describeEncodingStepUsingFunctionNotation({encodingStep, value, fnNotation, hideDetails}), rawValue
  );

export const describeEncodedSearchTermUsingFunctionNotationText = ({encodingSteps, searchTermAsSpecified, hideDetails}: EncodedSearchTermWithEncodingSteps &
  {
    hideDetails?: boolean;
  }
) =>
  describeEncodedSearchTermUsingFunctionNotationRaw({
    encodingSteps,
    hideDetails,
    rawValue: `“${searchTermAsSpecified}”`
  });

export const describeEncodedSearchTermUsingFunctionNotationHtml = ({encodingSteps, searchTermAsSpecified, hideDetails}: EncodedSearchTermWithEncodingSteps & {
    hideDetails?: boolean;
}
) =>
  describeEncodedSearchTermUsingFunctionNotationRaw({
    encodingSteps,
    hideDetails,
    rawValue: `“${searchTermAsSpecified}”`,
  });


export interface EncodedSearchTermWithEncodingSteps {
  originalDataType: InferredStringDataTypeName;
  searchTermAsSpecified: string;
  encodedSearchTerm: string;
  encodingSteps: EncodingStep[];
  validPrefixCharacters?: Set<string>;
  validSuffixCharacters?: Set<string>;
}
